/**
 * Shared, public-only query definitions. Used by route loaders on the server
 * (ensureQueryData) and by the page hooks in the browser, so both share the
 * exact same React Query keys and the browser hydrates without refetching.
 *
 * All queries use the publishable (anon) client, so row-level security applies,
 * and additionally filter explicitly to public / published rows.
 */
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { supabaseProjectManager, type Project } from "@/lib/supabaseProjectManager";
import type { HeroImage } from "@/hooks/useHeroImages";

export const LOAD_ERROR_MESSAGE =
  "Inhalte konnten gerade nicht geladen werden. Bitte versuchen Sie es später erneut.";

export interface Industry {
  name: string;
  coverImage: string;
  description?: string;
}

export interface ClientLogo {
  id: string;
  company_name: string;
  logo_url: string;
  display_order: number;
}

const isInline = (v: unknown): boolean => typeof v === "string" && v.startsWith("data:");

/** Replace an inline base64 cover with the first non-video gallery URL, else null. */
export function safeCover(cover: string | null | undefined, images?: string[] | null): string | null {
  if (cover && !isInline(cover)) return cover;
  const fallback = (images || []).find((img) => img && !img.startsWith("video::") && !isInline(img));
  return fallback ?? null;
}

export const projectsLeanQuery = (options: { publicOnly?: boolean; limit?: number } = {}) =>
  queryOptions({
    queryKey: ["projects", "lean", options.publicOnly, options.limit],
    queryFn: () => supabaseProjectManager.getProjectsLean(options),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

export const industriesQuery = () =>
  queryOptions({
    queryKey: ["industries"],
    queryFn: async (): Promise<Industry[]> => {
      const { data: projects, error } = await supabase
        .from("projects")
        .select("industry, cover_image, images")
        .eq("is_public", true)
        .not("industry", "is", null);
      if (error) throw error;
      const map = new Map<string, Industry>();
      projects?.forEach((p) => {
        if (p.industry && !map.has(p.industry)) {
          map.set(p.industry, { name: p.industry, coverImage: safeCover(p.cover_image, p.images) as string });
        }
      });
      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 5 * 60 * 1000,
  });

export const projectBySlugQuery = (slug: string) =>
  queryOptions({
    queryKey: ["project", slug],
    queryFn: async (): Promise<Project | null> => {
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("slug", slug)
        .eq("is_public", true)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        slug: data.slug,
        title: data.title,
        category: data.category as Project["category"],
        description: data.description,
        fullDescription: data.full_description || undefined,
        coverImage: safeCover(data.cover_image, data.images) as string,
        images: (data.images || []).filter((img) => !isInline(img)),
        client: data.client || undefined,
        year: data.year,
        services: data.services || [],
        hashtags: data.hashtags || [],
        industry: data.industry || undefined,
        isPublic: data.is_public || false,
        language: data.language as "de" | "en",
      };
    },
    staleTime: 5 * 60 * 1000,
  });

export const blogPostsQuery = () =>
  queryOptions({
    queryKey: ["blog-posts"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

export const latestBlogPostsQuery = () =>
  queryOptions({
    queryKey: ["latest-blog-posts"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("is_published", true)
        .order("published_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
  });

export const blogPostQuery = (slug: string) =>
  queryOptions({
    queryKey: ["blog-post", slug],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

export const clientLogosQuery = () =>
  queryOptions({
    queryKey: ["client-logos"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ClientLogo[]> => {
      const { data, error } = await supabase
        .from("client_logos")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return ((data || []) as ClientLogo[]).filter((l) => !isInline(l.logo_url));
    },
  });

export const heroImagesQuery = () =>
  queryOptions({
    queryKey: ["hero-images"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_hero_images")
        .select("id, page_identifier, image_url, image_type, project_id, updated_by, created_at, updated_at")
        .order("page_identifier");
      if (error) throw error;
      return (data as HeroImage[]).filter((h) => !isInline(h.image_url));
    },
  });

/** Server-side memo of public query results: the same lists are needed by every request that
 * renders a page, so a worker keeps them for a short while instead of asking the database again. */
const SERVER_MEMO_TTL_MS = 120_000;
const serverMemo = new Map<string, { data: unknown; expires: number }>();

/** Prefetch without ever throwing: a failed fetch leaves the query in error
 * state so the affected section shows the German error message. */
export async function safePrefetch(queryClient: QueryClient, opts: { queryKey: readonly unknown[] }) {
  const onServer = typeof window === "undefined";
  const memoKey = onServer ? JSON.stringify(opts.queryKey) : "";
  if (onServer) {
    const hit = serverMemo.get(memoKey);
    if (hit && hit.expires > Date.now()) {
      queryClient.setQueryData(opts.queryKey, hit.data);
      return hit.data;
    }
  }
  try {
    const data = await queryClient.ensureQueryData(opts as Parameters<QueryClient["ensureQueryData"]>[0]);
    if (onServer && data !== undefined) serverMemo.set(memoKey, { data, expires: Date.now() + SERVER_MEMO_TTL_MS });
    return data;
  } catch (err) {
    console.error("Loader prefetch failed:", err);
    return undefined;
  }
}
